import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import {
  UserCircleIcon,
  MapPinIcon,
  GlobeAltIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

import CategoryList from 'components/CategoryList';
import BrandCompactCover from 'components/BrandCardWrapper/BrandCompactCover';
import facebookIcon from 'assets/icons/socials/facebook_grey.svg';
import instagramIcon from 'assets/icons/socials/instagram_grey.svg';
import twitterIcon from 'assets/icons/socials/twitter_grey.svg';
import tiktokIcon from 'assets/icons/socials/tiktok_grey.svg';

const DummyRibbon = ({ label = t('listing.example') }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-10 rounded-2xl">
    <div
      className="absolute bg-amber-500 text-white text-[11px] font-bold uppercase tracking-widest text-center leading-[26px] h-[26px]"
      style={{
        top: '16px',
        right: '-32px',
        width: '160px',
        transform: 'rotate(45deg)',
        boxShadow: '0 2px 6px rgba(0,0,0,.18)',
      }}
    >
      {label}
    </div>
  </div>
);

const normUrl = (u) => {
  if (!u || typeof u !== 'string') return null;
  const s = u.trim();
  if (!s) return null;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

const SocialIconButton = ({ href, label, children }) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-500 hover:text-ink-strong hover:bg-paper transition"
    >
      {children}
    </a>
  ) : null;

const BrandCardList = ({ brand, hasNavigation, compact = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!brand) return null;

  const name = brand.username || brand.name || 'Unknown Brand';
  const eventsCount = brand?.activeUpcomingEventsCount ?? 0;
  const socials = brand?.socials ?? {};
  const { website, facebook, instagram, tiktok, twitter, others = [] } =
    socials;

  const handleClick = () => {
    if (hasNavigation && brand?.slug) navigate(`/brands/${brand.slug}`);
  };

  const locationLabel =
    brand.city && brand.country
      ? `${brand.city}, ${brand.country}`
      : brand.city || brand.country || '';

  // Below desktop: cover thumb with the logo on it, then name + categories,
  // location and a two-line description.
  if (compact) {
    return (
      <div
        role={hasNavigation ? 'button' : undefined}
        tabIndex={hasNavigation ? 0 : undefined}
        onClick={hasNavigation ? handleClick : undefined}
        onKeyDown={
          hasNavigation
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') handleClick();
              }
            : undefined
        }
        className={`relative bg-white rounded-2xl ring-1 ring-line shadow-card-listing p-2.5 flex gap-3 overflow-hidden ${
          hasNavigation ? 'cursor-pointer' : ''
        }`}
      >
        {brand.isDummy && <DummyRibbon />}

        <div className="relative w-[104px] h-[104px] rounded-xl overflow-hidden bg-paper shrink-0">
          <BrandCompactCover
            media={brand.media}
            name={name}
            // Lifted clear of the logo in the bottom-left corner.
            initialClass="text-[32px] -translate-y-[18px] translate-x-2"
          />
          <div className="absolute left-2 bottom-2 w-10 h-10 rounded-full overflow-hidden ring-2 ring-white bg-paper shadow-sm flex items-center justify-center">
            {brand.avatar ? (
              <img
                src={brand.avatar}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <UserCircleIcon className="w-6 h-6 text-gray-300" />
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 py-0.5">
          <div className="flex items-start gap-2">
            <h3 className="flex-1 min-w-0 font-extrabold text-[15px] leading-tight text-ink-strong truncate tracking-tight">
              {name}
            </h3>
            <div className="shrink-0">
              <CategoryList
                categories={brand.categories}
                onlyMacro
                withLabel
                tapToReveal
                macroIconClass="w-[18px] h-[18px]"
                containerClass="flex flex-row flex-nowrap items-center justify-end gap-[3px] overflow-visible"
              />
            </div>
          </div>
          {locationLabel && (
            <p className="mt-0.5 text-[12px] text-gray-500 truncate">
              {locationLabel}
            </p>
          )}
          <p className="mt-1 text-[12.5px] text-gray-700 leading-snug line-clamp-2 break-words">
            {brand.description || t('listing.no_description')}
          </p>
        </div>
      </div>
    );
  }

  const hasAnySocial =
    !!(normUrl(website) ||
      normUrl(facebook) ||
      normUrl(instagram) ||
      normUrl(twitter) ||
      normUrl(tiktok) ||
      (Array.isArray(others) && others.some((o) => normUrl(o?.url))));

  const eventsLine =
    eventsCount > 0 ? (
      <span className="text-[11px] text-gray-600 italic whitespace-nowrap">
        {t('listing.confirmed')}
        <span className="font-bold text-ink-strong not-italic">
          {eventsCount}
        </span>
        {eventsCount === 1 ? t('listing.event') : t('listing.events')}
      </span>
    ) : (
      <span className="text-[11px] text-gray-400 italic whitespace-nowrap">
        {t('listing.no_confirmations')}
      </span>
    );

  return (
    <div
      role={hasNavigation ? 'button' : undefined}
      tabIndex={hasNavigation ? 0 : undefined}
      onClick={hasNavigation ? handleClick : undefined}
      onKeyDown={
        hasNavigation
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }
          : undefined
      }
      className={`relative bg-white rounded-2xl ring-1 ring-line shadow-card-listing hover:shadow-lg transition px-4 py-3 ${
        hasNavigation ? 'cursor-pointer' : ''
      }
                  grid grid-cols-1 gap-4 items-center
                  lg:[grid-template-columns:56px_minmax(180px,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_180px]`}
    >
      {brand.isDummy && <DummyRibbon />}

      {/* Avatar */}
      <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full overflow-hidden ring-1 ring-line bg-paper shrink-0 flex items-center justify-center">
        {brand.avatar ? (
          <img
            src={brand.avatar}
            alt={name}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <UserCircleIcon className="w-8 h-8 text-gray-300" />
        )}
      </div>

      {/* Identity */}
      <div className="min-w-0">
        <h3 className="font-extrabold text-[15px] text-ink-strong truncate tracking-tight">
          {name}
        </h3>
        <p className="text-[12px] text-gray-500 truncate flex items-center gap-1">
          <MapPinIcon className="w-3 h-3 shrink-0" />
          {brand.city && brand.country
            ? `${brand.city}, ${brand.country}`
            : brand.city || brand.country || ''}
        </p>
      </div>

      {/* Description */}
      <p className="hidden lg:block text-[12.5px] text-gray-600 italic line-clamp-2 leading-relaxed">
        {brand.description || t('listing.no_description')}
      </p>

      {/* Categories */}
      <div className="hidden lg:flex">
        <CategoryList
          categories={brand.categories}
          onlyMacro
          withLabel
          macroIconClass="w-6 h-6"
          containerClass="flex flex-row flex-wrap items-center gap-x-2 gap-y-1 overflow-visible"
        />
      </div>

      {/* Right rail: socials + events */}
      <div className="flex items-center justify-end gap-3">
        {hasAnySocial && (
          <div className="flex items-center gap-1">
            <SocialIconButton href={normUrl(website)} label="Website">
              <GlobeAltIcon className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(instagram)} label="Instagram">
              <img src={instagramIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(facebook)} label="Facebook">
              <img src={facebookIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(twitter)} label="Twitter / X">
              <img src={twitterIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            <SocialIconButton href={normUrl(tiktok)} label="TikTok">
              <img src={tiktokIcon} alt="" className="w-3.5 h-3.5" />
            </SocialIconButton>
            {Array.isArray(others) &&
              others.map((o, i) => (
                <SocialIconButton
                  key={i}
                  href={normUrl(o?.url)}
                  label={o?.label || 'Link'}
                >
                  <LinkIcon className="w-3.5 h-3.5" />
                </SocialIconButton>
              ))}
          </div>
        )}
        <span className="hidden lg:inline-block w-px h-5 bg-line" />
        {eventsLine}
      </div>
    </div>
  );
};

export default BrandCardList;
